import React, { useEffect, useMemo, useState } from 'react';
import { useStore } from '../store';
import { STATUS, PRIORITY, TASK_TYPE, fmt } from '../utils';
import { ChevronDown, ChevronRight, CheckSquare, RefreshCw, Search } from 'lucide-react';
import { AvatarGroup } from '../components/UI/Avatar';
import { tasks as tasksApi } from '../api';

function getId(x) {
  return typeof x === 'string' ? x : x?._id;
}

function isAssignedToMe(task, meId) {
  if (!meId) return false;
  return (task.assignees || []).some(a => getId(a) === meId);
}

export default function MyTasksPage() {
  const { user, projects, fetchMyTasks, myTasks, myTasksLoading, openTask, updateTask } = useStore();
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('active');
  const [expandedProjects, setExpandedProjects] = useState(() => ({}));
  const [expandedParents, setExpandedParents] = useState(() => ({}));
  const [loadedSubtasksByParent, setLoadedSubtasksByParent] = useState(() => ({}));
  const [loadingSubtasksByParent, setLoadingSubtasksByParent] = useState(() => ({}));

  useEffect(() => {
    if (user?._id) fetchMyTasks({ userId: user._id });
  }, [user?._id]);

  const meId = user?._id;

  const projectById = useMemo(() => {
    const map = {};
    (projects || []).forEach(p => { map[p._id] = p; });
    return map;
  }, [projects]);

  const accentByProjectId = useMemo(() => {
    const map = {};
    (projects || []).forEach(p => { map[p._id] = p.color || '#4f63ff'; });
    return map;
  }, [projects]);

  const normalized = useMemo(() => {
    const q = search.trim().toLowerCase();
    return (myTasks || [])
      .filter(t => isAssignedToMe(t, meId))
      .filter(t => !q || (t.title || '').toLowerCase().includes(q))
      .filter(t => {
        if (statusFilter === 'all') return true;
        if (statusFilter === 'active') return !['done', 'cancelled'].includes(t.status);
        return t.status === statusFilter;
      })
      .sort((a, b) => (a.order || 0) - (b.order || 0));
  }, [myTasks, meId, search, statusFilter]);

  const grouped = useMemo(() => {
    // Build parents map (only tasks assigned to me are guaranteed here)
    const byId = {};
    normalized.forEach(t => { byId[t._id] = t; });

    // Group my subtasks by parentId, even if parent itself isn't assigned to me
    const subtasksByParent = {};
    normalized.forEach(t => {
      if (!t.parent) return;
      const parentId = getId(t.parent);
      if (!parentId) return;
      if (!subtasksByParent[parentId]) subtasksByParent[parentId] = [];
      subtasksByParent[parentId].push(t);
    });

    // For display: "parents" are either my root tasks, OR synthetic parent groups for my subtasks
    const roots = normalized.filter(t => !t.parent);

    const parentGroups = {};
    roots.forEach(t => { parentGroups[t._id] = { parent: t, subtasks: subtasksByParent[t._id] || [] }; });

    Object.entries(subtasksByParent).forEach(([parentId, subtasks]) => {
      if (parentGroups[parentId]) return;
      const parentStub = subtasks[0]?.parent && typeof subtasks[0].parent !== 'string'
        ? subtasks[0].parent
        : { _id: parentId, title: 'Родительская задача', number: null };
      parentGroups[parentId] = { parent: parentStub, subtasks };
    });

    // Group by project
    const byProject = {};
    Object.values(parentGroups).forEach(group => {
      const projectId = getId(group.parent?.project) || getId(group.subtasks?.[0]?.project) || 'unknown';
      if (!byProject[projectId]) byProject[projectId] = [];
      byProject[projectId].push(group);
    });

    // Stable ordering
    Object.values(byProject).forEach(arr => {
      arr.sort((a, b) => {
        const ao = a.parent?.order || 0;
        const bo = b.parent?.order || 0;
        if (ao !== bo) return ao - bo;
        return (a.parent?.createdAt || 0) > (b.parent?.createdAt || 0) ? -1 : 1;
      });
    });

    return byProject;
  }, [normalized]);

  const projectIdsOrdered = useMemo(() => {
    const ids = Object.keys(grouped);
    ids.sort((a, b) => {
      const pa = projectById[a]?.name || (a === 'unknown' ? 'Без проекта' : a);
      const pb = projectById[b]?.name || (b === 'unknown' ? 'Без проекта' : b);
      return pa.localeCompare(pb, 'ru');
    });
    return ids;
  }, [grouped, projectById]);

  const toggleProject = (projectId) => {
    setExpandedProjects(prev => ({ ...prev, [projectId]: !prev[projectId] }));
  };

  const toggleParent = (parentId) => {
    setExpandedParents(prev => ({ ...prev, [parentId]: !prev[parentId] }));
  };

  const ensureParentSubtasksLoaded = async (parentId) => {
    if (!parentId) return;
    if (loadedSubtasksByParent[parentId]) return;
    if (loadingSubtasksByParent[parentId]) return;

    setLoadingSubtasksByParent(prev => ({ ...prev, [parentId]: true }));
    try {
      const subtasks = await tasksApi.list({ parent: parentId });
      setLoadedSubtasksByParent(prev => ({ ...prev, [parentId]: subtasks || [] }));
    } catch {
      setLoadedSubtasksByParent(prev => ({ ...prev, [parentId]: [] }));
    } finally {
      setLoadingSubtasksByParent(prev => ({ ...prev, [parentId]: false }));
    }
  };

  const refresh = () => {
    if (user?._id) fetchMyTasks({ userId: user._id });
  };

  const toggleChecklistItem = async (task, itemIndex, projectId) => {
    const checklist = (task.checklist || []).map((c, idx) => idx === itemIndex ? { ...c, done: !c.done } : c);
    await updateTask(task._id, { checklist });
    // Ensure modal (if open) and myTasks list update via store.updateTask()
  };

  const click = () => {
    
  }

  return (
    <div className="flex-1 overflow-y-auto p-4 sm:p-6">
      <div className="max-w-5xl mx-auto">
        <div className="flex items-center justify-between gap-3 flex-wrap mb-5">
          <div>
            <h1 className="text-xl font-bold flex items-center gap-2">
              <CheckSquare className="w-5 h-5 text-brand-600" />
              Мои задачи
            </h1>
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">
              По всем проектам · {normalized.length} задач/подзадач
            </p>
          </div>

          <button onClick={refresh} disabled={myTasksLoading} className="btn-secondary py-1.5">
            <RefreshCw className={`w-4 h-4 ${myTasksLoading ? 'animate-spin' : ''}`} />
            Обновить
          </button>
        </div>

        <div className="flex items-center gap-2 mb-4 flex-wrap">
          <div className="relative">
            <Search className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
            <input
              className="input w-64 pl-9 text-sm"
              placeholder="Поиск по названию..."
              value={search}
              onChange={e => setSearch(e.target.value)}
            />
          </div>
          <select
            className="input w-auto text-sm"
            value={statusFilter}
            onChange={e => setStatusFilter(e.target.value)}
          >
            <option value="active">Активные</option>
            <option value="all">Все статусы</option>
            {Object.entries(STATUS).map(([key, { label }]) => (
              <option key={key} value={key}>{label}</option>
            ))}
          </select>
          <div className="flex-1" />
          {myTasksLoading && <span className="text-xs text-gray-400">Загрузка…</span>}
        </div>

        {(!myTasksLoading && projectIdsOrdered.length === 0) ? (
          <div className="card p-12 text-center">
            <p className="text-gray-400 text-sm">Задач не найдено</p>
            <p className="text-gray-300 dark:text-gray-600 text-xs mt-1">Попробуйте убрать поиск или обновить список</p>
          </div>
        ) : (
          <div className="space-y-3">
            {projectIdsOrdered.map(projectId => {
              const proj = projectById[projectId];
              const isOpen = expandedProjects[projectId] ?? true;
              const groups = grouped[projectId] || [];
              return (
                <div key={projectId} className="card overflow-hidden">
                  <button
                    className="w-full flex items-center gap-2 px-4 py-3 hover:bg-gray-50 dark:hover:bg-gray-800/40 transition-colors"
                    onClick={() => toggleProject(projectId)}
                  >
                    {isOpen ? <ChevronDown className="w-4 h-4 text-gray-400" /> : <ChevronRight className="w-4 h-4 text-gray-400" />}
                    <span className="text-base">{proj?.icon || (projectId === 'unknown' ? '📦' : '📋')}</span>
                    <span className="font-semibold text-sm truncate">{proj?.name || (projectId === 'unknown' ? 'Без проекта' : projectId)}</span>
                    <span className="ml-auto text-xs text-gray-400">{groups.reduce((s, g) => s + 1 + (g.subtasks?.length || 0), 0)}</span>
                  </button>

                  {isOpen && (
                    <div className="border-t border-gray-100 dark:border-gray-800">
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="border-b border-gray-50 dark:border-gray-800">
                            <th className="text-left px-4 py-2.5 text-xs font-medium text-gray-500 dark:text-gray-400">Задача</th>
                            <th className="text-left px-3 py-2.5 text-xs font-medium text-gray-500 dark:text-gray-400 w-28">Статус</th>
                            <th className="text-left px-3 py-2.5 text-xs font-medium text-gray-500 dark:text-gray-400 w-24">Приоритет</th>
                            <th className="text-left px-3 py-2.5 text-xs font-medium text-gray-500 dark:text-gray-400 w-28">Срок</th>
                            <th className="text-left px-3 py-2.5 text-xs font-medium text-gray-500 dark:text-gray-400 w-20">Кто</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-50 dark:divide-gray-800/50">
                          {groups.map(g => {
                            const parent = g.parent;
                            const parentId = getId(parent);
                            const loadedSubtasks = loadedSubtasksByParent[parentId] || [];
                            const combinedSubtasks = (() => {
                              const m = new Map();
                              (g.subtasks || []).forEach(t => m.set(t._id, t));
                              loadedSubtasks.forEach(t => m.set(t._id, t));
                              return Array.from(m.values());
                            })();
                            const canExpand = !!parentId;
                            const hasSubtasks = combinedSubtasks.length > 0;
                            const parentOpen = expandedParents[parentId] ?? true;
                            const s = STATUS[parent?.status];
                            const p = PRIORITY[parent?.priority];
                            const due = fmt.dueBadge(parent?.dueDate);
                            const accent = accentByProjectId[projectId] || '#4f63ff';
                            return (
                              <React.Fragment key={parentId}>
                                <tr className="hover:bg-gray-50 dark:hover:bg-gray-800/40 transition-colors">
                                  <td className="px-4 py-3">
                                    <div className="flex items-center gap-2">
                                      {canExpand ? (
                                        <button
                                          onClick={async () => {
                                            const nextOpen = !(expandedParents[parentId] ?? true);
                                            setExpandedParents(prev => ({ ...prev, [parentId]: nextOpen }));
                                            if (nextOpen) await ensureParentSubtasksLoaded(parentId);
                                          }}
                                          className="p-0.5 rounded hover:bg-gray-200 dark:hover:bg-gray-700"
                                        >
                                          {parentOpen ? <ChevronDown className="w-4 h-4 text-gray-400" /> : <ChevronRight className="w-4 h-4 text-gray-400" />}
                                        </button>
                                      ) : (
                                        <span className="w-5" />
                                      )}
                                      <span className="text-xs">{TASK_TYPE[parent?.type]?.icon}</span>
                                      <button
                                        className="font-medium text-left hover:text-brand-600 dark:hover:text-brand-400 transition-colors line-clamp-1"
                                        onClick={() => parent?._id && openTask(parent._id)}
                                      >
                                        {parent?.title || 'Задача'}
                                      </button>
                                      {parent?.number && <span className="text-xs text-gray-400 font-mono">#{parent.number}</span>}
                                    </div>
                                  </td>
                                  <td className="px-3 py-3">
                                    {parent?.status ? (
                                      <span className="tag" style={{ background: s?.color + '20', color: s?.color }}>
                                        <span className="w-1.5 h-1.5 rounded-full" style={{ background: s?.color }} />
                                        {s?.label}
                                      </span>
                                    ) : (
                                      <span className="text-xs text-gray-400">—</span>
                                    )}
                                  </td>
                                  <td className="px-3 py-3">
                                    {parent?.priority ? (
                                      <span className="text-sm" title={p?.label}>{p?.icon} <span className="text-xs text-gray-500">{p?.label}</span></span>
                                    ) : (
                                      <span className="text-xs text-gray-400">—</span>
                                    )}
                                  </td>
                                  <td className="px-3 py-3">
                                    {due && <span className={`tag text-[11px] ${due.cls}`}>{due.label}</span>}
                                  </td>
                                  <td className="px-3 py-3">
                                    {parent?.assignees?.length > 0 && <AvatarGroup users={parent.assignees} max={2} />}
                                  </td>
                                </tr>

                                {parent?.checklist?.length > 0 && parentOpen && parent.checklist.map((item, i) => (
                                  <tr key={`${parentId}-check-${i}`} className="bg-gray-50 dark:bg-gray-900/30">
                                    <td className="px-4 py-1 text-xs text-gray-500 dark:text-gray-400" colSpan={5}>
                                      <label className="inline-flex items-center gap-2 pl-7">
                                        <input
                                          type="checkbox"
                                          checked={!!item.done}
                                          onChange={(e) => {
                                            e.stopPropagation();
                                            toggleChecklistItem(parent, i, projectId);
                                          }}
                                          className="rounded-checkbox"
                                          style={{ '--checkbox-accent': accent, '--checkbox-border': accent }}
                                        />
                                        <span className={item.done ? 'line-through text-gray-400' : ''}>{item.text}</span>
                                      </label>
                                    </td>
                                  </tr>
                                ))}

                                {canExpand && parentOpen && combinedSubtasks
                                  .slice()
                                  .sort((a, b) => (a.order || 0) - (b.order || 0))
                                  .map(st => {
                                    const stS = STATUS[st.status];
                                    const stP = PRIORITY[st.priority];
                                    const stDue = fmt.dueBadge(st.dueDate);
                                    return (
                                      <React.Fragment key={st._id}>
                                        <tr className="bg-gray-50 dark:bg-gray-900/30 hover:bg-gray-100 dark:hover:bg-gray-800/50 transition-colors">
                                          <td className="px-4 py-2.5">
                                            <div className="flex items-center gap-2 pl-7">
                                              <span className="text-xs">{TASK_TYPE[st.type]?.icon}</span>
                                              <button
                                                className="text-left hover:text-brand-600 dark:hover:text-brand-400 transition-colors line-clamp-1"
                                                onClick={() => openTask(st._id)}
                                              >
                                                {st.title}
                                              </button>
                                              {st.number && <span className="text-xs text-gray-400 font-mono">#{st.number}</span>}
                                            </div>
                                          </td>
                                          <td className="px-3 py-2.5">
                                            <span className="tag" style={{ background: stS?.color + '20', color: stS?.color }}>
                                              <span className="w-1.5 h-1.5 rounded-full" style={{ background: stS?.color }} />
                                              {stS?.label}
                                            </span>
                                          </td>
                                          <td className="px-3 py-2.5">
                                            <span className="text-sm" title={stP?.label}>{stP?.icon} <span className="text-xs text-gray-500">{stP?.label}</span></span>
                                          </td>
                                          <td className="px-3 py-2.5">
                                            {stDue && <span className={`tag text-[11px] ${stDue.cls}`}>{stDue.label}</span>}
                                          </td>
                                          <td className="px-3 py-2.5">
                                            {st.assignees?.length > 0 && <AvatarGroup users={st.assignees} max={2} />}
                                          </td>
                                        </tr>
                                        {st.checklist?.length > 0 && st.checklist.map((item, i) => (
                                          <tr key={`${st._id}-check-${i}`} className="bg-gray-50 dark:bg-gray-900/40">
                                            <td className="px-4 py-1 text-xs text-gray-500 dark:text-gray-400" colSpan={5}>
                                              <label className="inline-flex items-center gap-2 pl-12">
                                                <input
                                                  type="checkbox"
                                                  checked={!!item.done}
                                                  onChange={(e) => {
                                                    e.stopPropagation();
                                                    toggleChecklistItem(st, i, projectId);
                                                  }}
                                                  className="rounded-checkbox"
                                                  style={{ '--checkbox-accent': accent, '--checkbox-border': accent }}
                                                />
                                                <span className={item.done ? 'line-through text-gray-400' : ''}>{item.text}</span>
                                              </label>
                                            </td>
                                          </tr>
                                        ))}
                                      </React.Fragment>
                                    );
                                  })}
                              </React.Fragment>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

